import React, { useEffect, useState } from "react";
import {
  LuClock,
  LuActivity,
  LuHeartPulse,
  LuQrCode,
  LuWallet,
  LuUsers,
  LuCpu,
  LuBot,
  LuNetwork,
  LuBellRing,
  LuFileHeart,
  LuZap,
  LuHelpingHand,
  LuStethoscope,
  LuBuilding2,
  LuShieldCheck,
  LuSiren,
  LuMapPin,
} from "react-icons/lu";
import MarketingLayout from "../components/site/MarketingLayout";
import PrimaryButton from "../components/site/PrimaryButton";
import Reveal from "../components/site/Reveal";
import BrandTagline from "../components/site/BrandTagline";
import AerMark, { highlightAer } from "../components/site/AerMark";
import LifesavrMark from "../components/site/LifesavrMark";
import QrifyMark from "../components/site/QrifyMark";
import warehouseEmergencyImg from "../assets/images/image.png";

const heroOrbitIcons = [
  { Icon: LuSiren, tone: "urgent", pos: "n" },
  { Icon: LuHeartPulse, tone: "sky", pos: "ne" },
  { Icon: LuBuilding2, tone: "navy", pos: "e" },
  { Icon: LuBot, tone: "gold", pos: "se" },
  { Icon: LuQrCode, tone: "sky", pos: "s" },
  { Icon: LuMapPin, tone: "navy", pos: "sw" },
  { Icon: LuBellRing, tone: "urgent", pos: "w" },
  { Icon: LuCpu, tone: "gold", pos: "nw" },
];

const carePath = [
  {
    title: "Outpatient Care",
    summary:
      "Immediate access to top-notch healthcare without hospital stays.",
    description:
      "Reduces hospital admission costs and resource strain, allowing healthcare providers to deliver efficient care while optimising operations.",
    Icon: LuStethoscope,
  },
  {
    title: "Inpatient Care",
    summary:
      "Comprehensive services with state-of-the-art technology and expert medical care.",
    description:
      "Supports seamless hospital coordination, specialised treatment pathways and continuous monitoring so patients receive the right care at every stage of recovery.",
    Icon: LuBuilding2,
  },
  {
    title: "Wellness programmes",
    summary:
      "Tailored plans integrating health monitoring and preventive care with risk management.",
    description:
      "Helps individuals and organisations stay ahead of health risks through proactive screening, lifestyle guidance and data-driven wellness engagement.",
    Icon: LuHeartPulse,
  },
  {
    title: "Insurance Collaboration",
    summary:
      "Strategic partnerships with insurance companies to optimise portfolio management.",
    description:
      "Enables smoother claims coordination, better risk insight and value-based care models that benefit insurers, providers and members alike.",
    Icon: LuShieldCheck,
  },
];

const challenges = [
  {
    title: "Rising Healthcare Costs",
    description:
      "With medical expenses skyrocketing, Svasth offers affordable, tech-driven solutions that ensure quality care without the financial burden.",
    Icon: LuWallet,
  },
  {
    title: "Chronic Disease Management",
    description:
      "60% of people over 45 live with chronic illness. Our tailored plans and real-time monitoring help manage these conditions effectively.",
    Icon: LuActivity,
  },
  {
    title: "Ageing Population",
    description:
      "As people live longer, the need for comprehensive, continuous care is growing. Svasth’s integrated services cater to the needs of individuals at every stage of life.",
    Icon: LuUsers,
  },
  {
    title: "Technology Integration",
    description:
      "With healthcare increasingly relying on technology, Svasth, a leading Abyss Tech, is at the forefront, using real-time data and AI to revolutionise health management.",
    Icon: LuCpu,
  },
];

const features = [
  {
    title: "AI-Driven Healthcare Support",
    description:
      "Get swift, accurate help when it matters most, easing your worries and enhancing vital, critical care.",
    Icon: LuBot,
  },
  {
    title: "Effortless Real-time Coordination",
    description:
      "Navigate emergencies with ease as we smooth out delays and chaos in health management processes.",
    Icon: LuNetwork,
  },
  {
    title: "Instant Alerts & Location Sharing",
    description:
      "Stay connected with your loved ones during medical emergencies and save time with instant notifications and real-time location sharing.",
    Icon: LuBellRing,
  },
  {
    title: "Up-to-Date Medical Info",
    description:
      "Delivered updated medical data to relevant stakeholders, ensuring you get the right care efficiently.",
    Icon: LuFileHeart,
  },
  {
    title: "Rapid Emergency Response",
    description: highlightAer(
      "Experience quicker Accelerated Emergency Response with real-time updates and efficient coordination, cutting down your wait time."
    ),
    Icon: LuZap,
  },
  {
    title: "Complete Care & Financial Ease",
    description:
      "Enjoy stress-free support and seamless management of medical costs, so you can focus on what matters.",
    Icon: LuHelpingHand,
  },
];

const Home = () => {
  const [activePath, setActivePath] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setActivePath((prev) => (prev + 1) % carePath.length);
    }, 3200);
    return () => window.clearInterval(id);
  }, []);

  return (
    <MarketingLayout>
      <section className="hm-hero">
        <div className="hm-hero__orbs" aria-hidden="true">
          <span className="hm-orb hm-orb--1" />
          <span className="hm-orb hm-orb--2" />
          <span className="hm-orb hm-orb--3" />
        </div>
        <div className="sv-container hm-hero__grid">
          <div className="hm-hero__copy">
            <span className="sv-eyebrow hm-hero__chip">
              <span className="hm-hero__chip-icon" aria-hidden="true">
                <LuClock size={14} strokeWidth={2.25} />
              </span>
              Every Second Counts!
            </span>
            <h1 className="sv-h1 hm-hero__title">
              Panicking over the{" "}
              <span className="sv-accent">Uncertainty</span> of Health
              Management?
            </h1>
            <p className="sv-lead hm-hero__lead">
              Ensure timely care with{" "}
              <span className="sv-urgent">AI-driven precision</span>, easing
              anxiety during critical moments through rapid response.
            </p>
            <div className="hm-hero__actions">
              <PrimaryButton to="/contact" variant="ghost-light">
                Contact Us
              </PrimaryButton>
            </div>
            <div className="hm-hero__tagline">
              <BrandTagline className="sv-brand-tagline--hero" withParens />
              <span>(Health is the only Wealth!)</span>
            </div>
          </div>

          <div className="hm-hero__visual" aria-hidden="true">
            <div className="hm-ai-core">
              <span className="hm-ai-core__glow" />
              <span className="hm-ai-core__ring hm-ai-core__ring--1" />
              <span className="hm-ai-core__ring hm-ai-core__ring--2" />
              <span className="hm-ai-core__ring hm-ai-core__ring--3" />
              <svg
                className="hm-ai-core__svg"
                viewBox="0 0 320 320"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g className="hm-ai-core__mesh" stroke="currentColor" strokeWidth="1.35">
                  <path d="M160 42 L210 68 L248 112 L258 168 L238 222 L190 258 L160 272 L130 258 L82 222 L62 168 L72 112 L110 68 Z" />
                  <path d="M160 42 L160 112 L210 68" opacity="0.65" />
                  <path d="M160 42 L160 112 L110 68" opacity="0.65" />
                  <path d="M248 112 L210 148 L160 112 L110 148 L72 112" opacity="0.7" />
                  <path d="M258 168 L210 148 L190 198 L238 222" opacity="0.65" />
                  <path d="M62 168 L110 148 L130 198 L82 222" opacity="0.65" />
                  <path d="M210 148 L160 168 L110 148" opacity="0.8" />
                  <path d="M190 198 L160 168 L130 198" opacity="0.8" />
                  <path d="M160 168 L160 232" opacity="0.75" />
                  <path d="M160 232 L190 258 M160 232 L130 258" opacity="0.6" />
                  <path d="M210 68 L210 148 M110 68 L110 148" opacity="0.5" />
                </g>
                <circle className="hm-ai-core__hub" cx="160" cy="168" r="28" />
              </svg>
              <div className="hm-ai-core__badges">
                {heroOrbitIcons.map(({ Icon, tone, pos }, i) => (
                  <span
                    key={pos}
                    className={`hm-ai-core__badge hm-ai-core__badge--${pos} hm-ai-core__badge--${tone}`}
                    style={{ animationDelay: `${-i * 0.35}s` }}
                  >
                    <Icon size={18} strokeWidth={2.1} />
                  </span>
                ))}
                <span className="hm-ai-core__badge hm-ai-core__badge--core hm-ai-core__badge--gold">
                  <LuNetwork size={22} strokeWidth={2} />
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="sv-section">
        <div className="sv-container">
          <Reveal>
            <div className="sv-section-head">
              <h2 className="sv-h2">
                <span className="sv-navy">Innovative Care</span> for Every Step
                of Your Health Journey.
              </h2>
              <p className="sv-lead">
                Svasth’s innovative approach covers every aspect of your health,
                from outpatient and inpatient care to holistic wellness programmes
                and our game-changing <AerMark /> system - <QrifyMark />.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <article className="hm-feature">
              <div className="hm-feature__glow" aria-hidden="true" />
              <div className="hm-feature__media hm-feature__media--photo">
                <img
                  src="/images/qrify-indian-emergency.png"
                  alt="Indian bystander scanning QRify on a bike front after a roadside accident"
                />
                <span className="hm-feature__media-badge">
                  <LuQrCode size={18} strokeWidth={2} />
                  Scan. Respond. Save.
                </span>
              </div>
              <div className="hm-feature__copy">
                <h3 className="sv-h2"><QrifyMark /> <AerMark /> System</h3>
                <p className="sv-lead">
                  A proprietary tech-driven <AerMark /> system that enables immediate assistance through a simple QR code scan or call. It bridges the critical gap between an incident and accelerated intervention, ensuring the fastest and most effective care when every second matters—on roads, in workplaces and at home.
                </p>
              </div>
            </article>
          </Reveal>
          <div className="hm-path" role="list">
            {carePath.map((item, i) => {
              const Icon = item.Icon;
              const isActive = activePath === i;
              return (
                <Reveal key={item.title} delayClass={`sv-d${(i % 4) + 1}`}>
                  <article
                    role="listitem"
                    className={`hm-path__item${isActive ? " is-active" : ""}`}
                    onMouseEnter={() => setActivePath(i)}
                    onFocus={() => setActivePath(i)}
                    tabIndex={0}
                  >
                    <div className="hm-path__icon" aria-hidden="true">
                      <Icon size={26} strokeWidth={1.75} />
                    </div>
                    <h3 className="hm-path__title">{item.title}</h3>
                    <p className="hm-path__summary">{item.summary}</p>
                    <p className="hm-path__body">{item.description}</p>
                    <span className="hm-path__glow" aria-hidden="true" />
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="sv-section">
        <div className="sv-container">
          <Reveal>
            <div className="sv-section-head">
              <h2 className="sv-h2">
                Redefining Healthcare for a{" "}
                <span className="sv-navy">Healthier Tomorrow</span>
              </h2>
              <p className="sv-lead">
                Accessible, personalised care powered by technology to meet the
                challenges of today’s world.
              </p>
            </div>
          </Reveal>
          <div className="hm-challenge">
            {challenges.map((item, i) => {
              const Icon = item.Icon;
              return (
                <Reveal key={item.title} delayClass={`sv-d${(i % 4) + 1}`}>
                  <article className="hm-challenge__row">
                    <div className="hm-challenge__icon">
                      <Icon size={26} strokeWidth={1.75} />
                    </div>
                    <div>
                      <h3 className="sv-h3">{item.title}</h3>
                      <p className="sv-body">{item.description}</p>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
          <Reveal>
            <div className="hm-mid-cta">
              <p className="sv-lead">
                Discover the solution that empowers you to confidently overcome
                challenges for a healthier future.
              </p>
              <PrimaryButton to="/contact">Contact Us</PrimaryButton>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="sv-section">
        <div className="sv-container hm-stories">
          <Reveal>
            <div className="sv-section-head">
              <h2 className="sv-h2">When every second of care matters</h2>
            </div>
          </Reveal>
          <Reveal>
            <article className="hm-story">
              <div className="hm-story__copy">
                <span className="sv-eyebrow hm-story__badge">
                  <span className="hm-story__badge-dot" aria-hidden="true" />
                  <LuHelpingHand size={15} strokeWidth={2.25} aria-hidden="true" />
                  <LifesavrMark>Lifesavr’s</LifesavrMark> Swift Aid
                </span>
                <h3 className="sv-h2">Accelerated Response to Medical Emergencies</h3>
                <p className="sv-body">
                  In a serious car accident, a driver is left
                  vulnerable. <AerMark /> is initiated thanks to a{" "}
                  <LifesavrMark /> on the scene. Their quick action using our
                  solution ensures critical aid in those vital moments.
                </p>
              </div>
              <div className="hm-story__visual hm-story__visual--photo">
                <img
                  src="/images/story-accident-response.png"
                  alt="Indian rider after a roadside accident while a friend contacts QRify for emergency help"
                />
              </div>
            </article>
          </Reveal>
          <Reveal delayClass="sv-d2">
            <article className="hm-story hm-story--flip">
              <div className="hm-story__visual hm-story__visual--photo">
                <img
                  src={warehouseEmergencyImg}
                  alt="Warehouse worker experiencing breathing distress while a supervisor calls for emergency help"
                />
              </div>
              <div className="hm-story__copy">
                <span className="sv-eyebrow hm-story__badge hm-story__badge--urgent">
                  <span className="hm-story__badge-dot" aria-hidden="true" />
                  <LuSiren size={15} strokeWidth={2.25} aria-hidden="true" />
                  Urgent Care Delivered
                </span>
                <h3 className="sv-h2"><AerMark /> for Critical Illness</h3>
                <p className="sv-body">
                  During a critical workplace emergency, a colleague’s quick intervention
                  ensures an office worker with chronic disease receives immediate
                  care. Our solution empowers <LifesavrMark>Lifesavrs</LifesavrMark>{" "}
                  with the tools for swift
                  action in everyday workplaces.
                </p>
              </div>
            </article>
          </Reveal>
        </div>
      </section>

      <section className="sv-section">
        <div className="sv-container">
          <Reveal>
            <div className="sv-section-head">
              <h2 className="sv-h2">
                AI Meets Health <span className="sv-navy">Management</span>
              </h2>
              <p className="sv-lead">
                Combining cutting-edge technology with Health and Wellness,
                Svasth offers peace of mind and safety in every health condition.
                Embrace a safer, more balanced future with our health management
                protocol.
              </p>
            </div>
          </Reveal>
          <div className="hm-ai">
            {features.map((item, i) => {
              const Icon = item.Icon;
              return (
                <Reveal key={item.title} delayClass={`sv-d${(i % 4) + 1}`}>
                  <article className="hm-ai__card">
                    <div className="hm-ai__icon">
                      <Icon size={24} strokeWidth={1.75} />
                    </div>
                    <h3 className="hm-ai__title">{item.title}</h3>
                    <p>{item.description}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="sv-section">
        <div className="sv-container">
          <Reveal>
            <div className="hm-cta">
              <div className="hm-cta__pulse" aria-hidden="true" />
              <div className="hm-cta__copy">
                <h2 className="sv-h2">
                  Join us in shaping a healthier, more connected future.
                </h2>
                <p className="sv-lead">
                  At Svasth, we’re not just keeping up with the changing times —
                  we’re leading the way.
                </p>
                <PrimaryButton to="/contact" pulse>
                  Get in touch
                </PrimaryButton>
              </div>
              <div className="hm-cta__visual hm-cta__visual--heart">
                <img
                  src="/images/cta-digital-heart.png"
                  alt="Digital wireframe heart representing connected AI-driven care"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default Home;
